import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { Form } from 'softcat:components/form.tsx';
import { CertificateType } from 'softcat:config/certificate-type.ts';
import { CertificateService } from 'softcat:services/certificate.ts';
import * as z from 'zod';

export const CertificatesCreate = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [onCreateError, setOnCreateError] = useState('');
    const certificateFileExtension = '.pem';

    const CreateCertificateSchema = z.object({
        active: z.boolean(),
        certificate: z.instanceof(File, { message: intl.formatMessage({ id: 'CERTIFICATES_CREATE_PAGE_FORM_CERTIFICATE_VALIDATION_REQUIRED' }) }).refine(
            (file) => {
                return file.name.endsWith(certificateFileExtension);
            },
            { message: intl.formatMessage({ id: 'FORM_FILE_UPLOAD_VALIDATION_EXTENSION' }, { extensions: certificateFileExtension }) },
        ),
        name: z.string().regex(/^[\dA-Za-z]+$/, { message: intl.formatMessage({ id: 'CERTIFICATES_CREATE_PAGE_FORM_NAME_VALIDATION_ALPHANUMERIC' }) }),
        type: z.nativeEnum(CertificateType),
    });

    const onSubmit = async (values: z.infer<typeof CreateCertificateSchema>) => {
        setSubmitting(true);
        setOnCreateError('');

        try {
            await CertificateService.create({ active: values.active, file: values.certificate, name: values.name, type: values.type });

            setSubmitting(false);

            return navigate('/certificates');
        } catch (error) {
            setSubmitting(false);
            setOnCreateError((error as Error).message);
        }
    };

    const defaultValues = useMemo(() => {
        return { active: false, certificate: undefined, name: '', type: CertificateType.ALL };
    }, []);

    return (
        <Form<z.infer<typeof CreateCertificateSchema>>
            defaultValues={defaultValues}
            mode={'onBlur'}
            onSubmit={onSubmit}
            resolver={zodResolver(CreateCertificateSchema)}
        >
            <div className="space-y-3 px-4">
                <Form.Header
                    description={<FormattedMessage id={'CERTIFICATES_CREATE_PAGE_DESCRIPTION'} />}
                    title={<FormattedMessage id={'CERTIFICATES_CREATE_PAGE_HEADING'} />}
                />

                <Form.FileUpload
                    accept={certificateFileExtension}
                    label={<FormattedMessage id={'CERTIFICATES_CREATE_PAGE_FORM_CERTIFICATE_LABEL'} />}
                    name={'certificate'}
                />

                <Form.Input label={<FormattedMessage id={'CERTIFICATES_FORM_NAME_LABEL'} />} name={'name'} />

                <Form.RadioGroup
                    label={<FormattedMessage id={'CERTIFICATES_FORM_TYPE_LABEL'} />}
                    name={'type'}
                    options={Object.values(CertificateType).map((type) => {
                        return { label: type, value: type };
                    })}
                />

                <Form.Checkbox label={<FormattedMessage id={'CERTIFICATES_FORM_ACTIVE_LABEL'} />} name={'active'} />

                <Form.Footer
                    isSubmitting={submitting}
                    onCancel={() => {
                        return navigate('/certificates');
                    }}
                    submitError={onCreateError && <FormattedMessage id={'CERTIFICATES_CREATE_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onCreateError }} />}
                    submitText={<FormattedMessage id={'BUTTON_CREATE'} />}
                />
            </div>
        </Form>
    );
};
